import { useState, useEffect } from "react";
import {
  Button,
  Card,
  Form,
  Grid,
  Input,
  Steps,
  Typography,
  message,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  BankOutlined,
  TagOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import logoIcon from "../../assets/images/eko-favicon.png";

const { Title, Text } = Typography;
const { Step } = Steps;
const { useBreakpoint } = Grid;

const SignupComponent = (props) => {
  const { handleSignup, submitting, handleCheckTenant, checkingTenant } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = !!screens.md && !screens.lg;

  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const savedData = sessionStorage.getItem('signupData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData(parsedData);
      form.setFieldsValue(parsedData);
      // If coming back to correct email, stay on step 0
      setCurrentStep(0);
    }
  }, [form]);

  const handleNextStep = async () => {
    try {
      const values = await form.validateFields();
      await handleCheckTenant(values.org_acronym);
      setFormData({ ...formData, ...values });
      setCurrentStep(1);
    } catch (error) {
      if (error.message === 'tenant_exists') {
        form.setFields([{ name: 'org_acronym', errors: ['Organization slug is already taken.'] }]);
      } else if (error.message === 'check_failed') {
        message.error("Failed to check tenant. Please try again.");
      } else {
        console.log("Validation failed:", error);
      }
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(0);
  };

  const onFinish = async (values) => {
    if (!executeRecaptcha) {
      message.error("reCAPTCHA not loaded. Please try again.");
      return;
    }

    try {
      const token = await executeRecaptcha('signup');
      const finalData = { ...formData, ...values, recaptcha_token: token };
      handleSignup(finalData);
    } catch (error) {
      console.error("reCAPTCHA execution failed:", error);
      message.error("Failed to verify reCAPTCHA. Please try again.");
    }
  };

  const handleOrgNameChange = (e) => {
    const value = e.target.value;
    const slug = value.replace(/[^a-zA-Z]/g, '').toLowerCase();
    form.setFieldsValue({ org_acronym: slug });
  };

  const renderStep1 = () => (
    <>
      <Form.Item
        name="org_email"
        label={<span style={styles.label(isMobile)}>Organization Email</span>}
        rules={[
          { required: true, message: "Please input your organization email!" },
          { type: "email", message: "Please enter a valid email!" },
        ]}
        style={styles.formItem}
        validateTrigger="onBlur"
      >
        <Input
          size="large"
          placeholder="Enter your organization email"
          prefix={<MailOutlined style={styles.inputIcon} />}
          style={styles.input(isMobile)}
          onChange={(e) => {
            if (form.getFieldError('org_email').length > 0) {
              form.setFields([{ name: 'org_email', errors: [] }]);
            }
          }}
        />
      </Form.Item>

      <Form.Item
        name="org_name"
        label={<span style={styles.label(isMobile)}>Organization Name</span>}
        rules={[
          {
            validator: (_, value) => {
              if (!value || value.trim() === '') {
                return Promise.reject(new Error('Please input your organization name!'));
              }
              if (!/^[a-zA-Z\s]+$/.test(value)) {
                return Promise.reject(new Error('Name can only contain letters and spaces.'));
              }
              return Promise.resolve();
            },
          },
        ]}
        style={styles.formItem}
      >
        <Input
          size="large"
          placeholder="Enter your organization name"
          prefix={<BankOutlined style={styles.inputIcon} />}
          style={styles.input(isMobile)}
          onChange={handleOrgNameChange}
        />
      </Form.Item>

      <Form.Item
        name="org_acronym"
        label={<span style={styles.label(isMobile)}>Organization Acronym (Slug)</span>}
        rules={[
          {
            validator: (_, value) => {
              if (!value || value.trim() === '') {
                return Promise.reject(new Error('Please input your organization acronym!'));
              }
              if (!/^[a-z]+$/.test(value)) {
                return Promise.reject(new Error('Slug must contain only lowercase alphabets!'));
              }
              return Promise.resolve();
            }
          }
        ]}
        style={styles.formItem}
      >
        <Input
          size="large"
          placeholder="Enter your organization acronym"
          prefix={<TagOutlined style={styles.inputIcon} />}
          style={styles.input(isMobile)}
        />
      </Form.Item>

      <Form.Item style={styles.submitFormItem}>
        <Button
          type="primary"
          onClick={handleNextStep}
          block
          size="large"
          style={styles.button(isMobile)}
          loading={checkingTenant}
        >
          Next
        </Button>
      </Form.Item>
    </>
  );

  const renderStep2 = () => (
    <>
      <Form.Item
        name="username"
        label={<span style={styles.label(isMobile)}>Username</span>}
        rules={[
          { required: true, message: "Please input your username!" },
        ]}
        style={styles.formItem}
      >
        <Input
          size="large"
          placeholder="Enter your username"
          prefix={<UserOutlined style={styles.inputIcon} />}
          style={styles.input(isMobile)}
        />
      </Form.Item>

      <Form.Item
        name="password"
        label={<span style={styles.label(isMobile)}>Password</span>}
        rules={[
          { required: true, message: "Please input your password!" },
          { min: 6, message: "Password must be at least 6 characters!" },
        ]}
        style={styles.formItem}
      >
        <Input.Password
          size="large"
          placeholder="Enter your password"
          prefix={<LockOutlined style={styles.inputIcon} />}
          style={styles.input(isMobile)}
        />
      </Form.Item>

      <Form.Item
        name="confirm_password"
        label={<span style={styles.label(isMobile)}>Confirm Password</span>}
        rules={[
          { required: true, message: "Please confirm your password!" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('The two passwords do not match!'));
            },
          }),
        ]}
        style={styles.formItem}
        validateTrigger="onBlur"
      >
        <Input.Password
          size="large"
          placeholder="Confirm your password"
          prefix={<LockOutlined style={styles.inputIcon} />}
          style={styles.input(isMobile)}
          onChange={(e) => {
            if (form.getFieldError('confirm_password').length > 0) {
              form.setFields([{ name: 'confirm_password', errors: [] }]);
            }
          }}
        />
      </Form.Item>

      <Form.Item style={styles.submitFormItem}>
        <div style={styles.buttonGroup}>
          <Button
            onClick={handlePreviousStep}
            size="large"
            style={styles.backButton(isMobile)}
            icon={<ArrowLeftOutlined />}
          >
            Back
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            size="large"
            style={{ ...styles.button(isMobile), flex: 1 }}
          >
            Sign Up
          </Button>
        </div>
      </Form.Item>
    </>
  );

  return (
    <div style={styles.container(isMobile)}>
      <Card style={styles.card(isMobile, isTablet)} bordered={false}>
        <div style={styles.cardContent(isMobile, isTablet)}>
          {/* Header Section */}
          <div style={styles.header(isMobile)}>
            <img src={logoIcon} alt="Company Logo" style={styles.logo(isMobile)} />
            <Title level={2} style={styles.title(isMobile)}>
              Create Account
            </Title>
            <Text style={styles.subtitle(isMobile)}>
              Create account to start a free trial
            </Text>
          </div>

          {/* Steps indicator */}
          <Steps
            current={currentStep}
            direction={isMobile ? "vertical" : "horizontal"}
            size={isMobile ? "small" : "default"}
            style={styles.steps(isMobile)}
          >
            <Step title="Organization" />
            <Step title="Account" />
          </Steps>

          {/* Signup form */}
          <Form
            form={form}
            name="signup_form"
            layout="vertical"
            onFinish={onFinish}
            style={styles.form}
            initialValues={formData}
            hideRequiredMark={true}
          >
            {currentStep === 0 ? renderStep1() : renderStep2()}
          </Form>
        </div>
      </Card>
    </div>
  );
};

// Styling for the page
const styles = {
  container: (isMobile) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100dvh",
    width: "100%",
    minWidth: "100vw",
    padding: isMobile ? "16px 12px" : "20px",
    boxSizing: "border-box",
  }),
  card: (isMobile, isTablet) => ({
    width: "100%",
    maxWidth: isMobile ? "100%" : "550px",
    minWidth: isMobile ? "100%" : "550px",
    padding: 0,
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
    borderRadius: "16px",
    backgroundColor: "#fff",
    overflow: "hidden",
  }),
  cardContent: (isMobile, isTablet) => ({
    padding: isMobile ? "24px 16px" : isTablet ? "36px 28px" : "48px 40px",
  }),
  header: (isMobile) => ({
    textAlign: "center",
    marginBottom: isMobile ? "24px" : "32px",
  }),
  logo: (isMobile) => ({
    display: "block",
    margin: "0 auto 16px auto",
    height: isMobile ? 56 : 80,
    width: "auto",
    maxWidth: "100%",
  }),
  title: (isMobile) => ({
    margin: "0 0 8px 0",
    color: "#1a1a1a",
    fontWeight: "600",
    fontSize: isMobile ? "24px" : "28px",
    lineHeight: isMobile ? "30px" : "36px",
  }),
  subtitle: (isMobile) => ({
    color: "#666",
    fontSize: isMobile ? "14px" : "16px",
    margin: 0,
  }),
  steps: (isMobile) => ({
    marginBottom: isMobile ? "24px" : "32px",
  }),
  form: {
    width: "100%",
  },
  formItem: {
    marginBottom: "24px",
  },
  submitFormItem: {
    marginBottom: 0,
    marginTop: "32px",
  },
  label: (isMobile) => ({
    fontWeight: "600",
    color: "#333",
    fontSize: isMobile ? "13px" : "14px",
  }),
  input: (isMobile) => ({
    borderRadius: "8px",
    border: "1px solid #d9d9d9",
    fontSize: isMobile ? "15px" : "16px",
    transition: "all 0.3s ease",
    height: isMobile ? "44px" : "48px",
    width: "100%",
  }),
  inputIcon: {
    color: "#999",
  },
  button: (isMobile) => ({
    width: "100%",
    backgroundColor: "#1890ff",
    borderColor: "#1890ff",
    fontWeight: "600",
    fontSize: isMobile ? "15px" : "16px",
    height: isMobile ? "44px" : "48px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
    transition: "all 0.3s ease",
  }),
  backButton: (isMobile) => ({
    width: "100%",
    fontWeight: "600",
    fontSize: isMobile ? "15px" : "16px",
    height: isMobile ? "44px" : "48px",
    borderRadius: "8px",
    marginRight: "12px",
    minWidth: isMobile ? "84px" : "96px",
  }),
  buttonGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

export default SignupComponent;
